import './index.css'
import { Button, Dropdown, Space, Table, Tooltip } from 'antd'
import type { ItemType } from 'antd/lib/menu/hooks/useItems'
import { useCheckPermissions } from 'hooks/useCheckPermission'
import type { Key } from 'react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { WorkflowRecord } from '../../types'
import type {
  RecordsTableActions,
  RecordsTableActionsResolverUtils,
  RecordsTableDirectAction,
  RecordsTableMenuItemAction,
  RecordsTableMenuTrigger,
  RecordsTablePagination,
  RecordsTableProps,
} from './types'

const authorizationDisabledMessage =
  "You don't have permission to perform this action."

const actionsResolverUtils: RecordsTableActionsResolverUtils = {
  include: (included, config) => (included ? config : undefined),
}

function configuredActions<Action>(actions: Array<Action | undefined>) {
  return actions.filter((action): action is Action => action !== undefined)
}

function actionDisabledMessages(
  action: RecordsTableDirectAction,
  authorized: boolean
) {
  return [
    authorized ? undefined : authorizationDisabledMessage,
    action.disabled === false ? undefined : action.disabled.message,
  ].filter((message): message is string => message !== undefined)
}

function menuItem(
  action: RecordsTableMenuItemAction,
  authorized: boolean,
  key: Key
): ItemType {
  const disabledMessages = actionDisabledMessages(action, authorized)

  return {
    disabled: disabledMessages.length > 0,
    key,
    label: (
      <Tooltip
        placement="right"
        title={disabledMessages.length ? disabledMessages.join(' ') : undefined}
      >
        <span className="records-table-menu-item">{action.label}</span>
      </Tooltip>
    ),
    onClick: () => void action.onClick(),
  }
}

function MenuRecordAction({
  groups,
  label,
  trigger,
}: {
  groups: Array<{ items: ItemType[]; label: string }>
  label: string
  trigger: RecordsTableMenuTrigger
}) {
  const button = (
    <Button
      className="records-table-action"
      disabled={trigger.disabled !== false}
      type="text"
    >
      {label}
    </Button>
  )

  if (trigger.disabled !== false) {
    return (
      <Tooltip title={trigger.disabled.message}>
        <span className="records-table-action-wrapper">{button}</span>
      </Tooltip>
    )
  }

  return (
    <Dropdown
      menu={{
        items: groups.map(({ items, label: groupLabel }) => ({
          children: items,
          key: groupLabel,
          label: groupLabel,
          type: 'group',
        })),
      }}
      overlayClassName="records-table-action-menu"
      trigger={['click']}
    >
      {button}
    </Dropdown>
  )
}

function RecordActions({
  actions,
  record,
}: {
  actions: RecordsTableActions
  record: WorkflowRecord
}) {
  const can = useCheckPermissions(record)
  const workflowAdvance = configuredActions(actions.workflow?.advance ?? [])
  const workflowRevert = configuredActions(actions.workflow?.revert ?? [])
  const sharePointItems = configuredActions(actions.sharePoint?.items ?? [])
  const workflow =
    actions.workflow &&
    (workflowAdvance.length > 0 || workflowRevert.length > 0)
      ? {
          ...actions.workflow,
          advance: workflowAdvance,
          revert: workflowRevert,
        }
      : undefined
  const sharePoint =
    actions.sharePoint && sharePointItems.length > 0
      ? { ...actions.sharePoint, items: sharePointItems }
      : undefined
  const visibleActions: Array<{
    action: RecordsTableDirectAction
    label: string
    name: 'edit' | 'lifecycle'
  }> = []
  if (actions.edit) {
    visibleActions.push({ action: actions.edit, label: 'Edit', name: 'edit' })
  }
  if (actions.lifecycle) {
    const action = actions.lifecycle.active
      ? actions.lifecycle.remove
      : actions.lifecycle.restore
    visibleActions.push({
      action,
      label: actions.lifecycle.active ? 'Remove' : 'Restore',
      name: 'lifecycle',
    })
  }

  return (
    <Space
      className="records-table-actions"
      direction="vertical"
      onClick={(event) => event.stopPropagation()}
      size={8}
    >
      {workflow && (
        <MenuRecordAction
          groups={[
            ...(workflow.advance.length > 0
              ? [
                  {
                    items: workflow.advance.map((action, index) =>
                      menuItem(
                        action,
                        can(action.permission),
                        `${record.id}:workflow:advance:${index}`
                      )
                    ),
                    label: 'Advance',
                  },
                ]
              : []),
            ...(workflow.revert.length > 0
              ? [
                  {
                    items: workflow.revert.map((action, index) =>
                      menuItem(
                        action,
                        can(action.permission),
                        `${record.id}:workflow:revert:${index}`
                      )
                    ),
                    label: 'Revert',
                  },
                ]
              : []),
          ]}
          label="Workflow"
          trigger={workflow}
        />
      )}
      {sharePoint && (
        <MenuRecordAction
          groups={[
            {
              items: sharePoint.items.map((action, index) =>
                menuItem(
                  action,
                  can(action.permission),
                  `${record.id}:sharepoint:${index}`
                )
              ),
              label: 'SharePoint',
            },
          ]}
          label="SharePoint"
          trigger={sharePoint}
        />
      )}
      {visibleActions.map(({ action, label, name }) => {
        const disabledMessages = actionDisabledMessages(
          action,
          can(action.permission)
        )
        const button = (
          <Button
            className="records-table-action"
            disabled={disabledMessages.length > 0}
            onClick={() => void action.onClick()}
            type="text"
          >
            {label}
          </Button>
        )

        return disabledMessages.length ? (
          <Tooltip key={name} title={disabledMessages.join(' ')}>
            <span className="records-table-action-wrapper">{button}</span>
          </Tooltip>
        ) : (
          <span className="records-table-action-wrapper" key={name}>
            {button}
          </span>
        )
      })}
    </Space>
  )
}

/**
 * Presents workflow records with the controls and table behavior used by the
 * Records page.
 */
function RecordsTable({
  actions,
  columns,
  createAction,
  expandable,
  loading,
  onReload,
  records,
}: RecordsTableProps) {
  const [currentRecordCount, setCurrentRecordCount] = useState(records.length)
  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([])
  const [pageSize, setPageSize] = useState(25)
  const hasActiveFilters = useRef(false)
  const pendingScrollRowKey = useRef<Key | undefined>(undefined)
  const tableContainer = useRef<HTMLDivElement>(null)

  useEffect(() => {
    /*
     * Ant preserves its filters when dataSource changes. Keep the matching
     * count as well so a filtered-empty table does not regain horizontal
     * scrolling while refreshed records are still filtered out.
     */
    if (!hasActiveFilters.current) setCurrentRecordCount(records.length)
  }, [records])

  /*
   * Expanded details can render below Ant's 60vh table viewport. Once the
   * controlled keys have rendered, align the triggering record within the
   * table body instead of moving the page with scrollIntoView.
   */
  useLayoutEffect(() => {
    const rowKey = pendingScrollRowKey.current
    if (rowKey === undefined || !expandedRowKeys.includes(rowKey)) return

    pendingScrollRowKey.current = undefined

    const container = tableContainer.current
    const tableBody = container?.querySelector<HTMLElement>('.ant-table-body')
    const recordRow = [
      ...(container?.querySelectorAll('tr[data-row-key]') ?? []),
    ].find((row) => row.getAttribute('data-row-key') === String(rowKey))

    if (!tableBody || !recordRow) return

    const offset =
      recordRow.getBoundingClientRect().top -
      tableBody.getBoundingClientRect().top
    const top = tableBody.scrollTop + offset
    if (typeof tableBody.scrollTo === 'function') {
      tableBody.scrollTo({
        behavior: 'smooth',
        left: tableBody.scrollLeft,
        top,
      })
    } else {
      tableBody.scrollTop = top
    }
  }, [expandedRowKeys])

  const resolvedExpandable = useMemo(
    () => ({
      ...expandable,
      expandRowByClick: true,
      expandedRowKeys,
      onExpandedRowsChange: (keys: readonly Key[]) => {
        expandable.onExpandedRowsChange?.(keys)
        /*
         * The key added to the controlled set identifies an expansion. A
         * collapse adds no key, so it must not trigger another scroll.
         */
        pendingScrollRowKey.current = keys.find(
          (key) => !expandedRowKeys.includes(key)
        )
        setExpandedRowKeys([...keys])
      },
    }),
    [expandable, expandedRowKeys]
  )
  const actionsByRecord = useMemo(
    () =>
      new Map(
        records.map((record) => [record, actions(record, actionsResolverUtils)])
      ),
    [actions, records]
  )
  const resolvedColumns = useMemo(() => {
    const hasVisibleAction = [...actionsByRecord.values()].some(
      (recordActions) =>
        recordActions.edit !== undefined ||
        recordActions.lifecycle !== undefined ||
        Boolean(
          recordActions.workflow?.advance.some(
            (action) => action !== undefined
          ) ||
            recordActions.workflow?.revert.some(
              (action) => action !== undefined
            ) ||
            recordActions.sharePoint?.items.some(
              (action) => action !== undefined
            )
        )
    )

    if (!hasVisibleAction) return columns

    return [
      ...columns,
      {
        fixed: 'right' as const,
        key: 'actions',
        render: (_value: unknown, record: WorkflowRecord) => (
          <RecordActions
            actions={actionsByRecord.get(record) ?? {}}
            record={record}
          />
        ),
        title: 'Actions',
        width: 120,
      },
    ]
  }, [actionsByRecord, columns])
  const pagination: RecordsTablePagination = {
    onChange: (_page, nextPageSize) => setPageSize(nextPageSize),
    pageSize,
    pageSizeOptions: [25, 50, 100],
    position: ['bottomRight'],
    showQuickJumper: true,
    showSizeChanger: true,
    showTotal: (total, [from, to]) => (
      <div style={{ marginRight: 8 }}>
        {from}-{to} of {total}
      </div>
    ),
  }
  const createDisabled = createAction?.disabled ?? false

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem 1.5rem',
          justifyContent: 'space-between',
        }}
      >
        <div>
          {createAction && (
            <Tooltip
              title={
                createDisabled === false ? undefined : createDisabled.message
              }
            >
              <Button
                disabled={createDisabled !== false || loading}
                loading={loading}
                onClick={createAction.onClick}
                type="primary"
              >
                {createAction.label}
              </Button>
            </Tooltip>
          )}
        </div>
        <div
          style={{
            alignItems: 'center',
            display: 'inline-flex',
            flexWrap: 'wrap',
            gap: '0.5rem 1rem',
          }}
        >
          {records.length > 1 && expandedRowKeys.length > 0 && (
            <Button onClick={() => setExpandedRowKeys([])}>Collapse All</Button>
          )}
          <Button
            disabled={loading}
            id="reload-btn"
            loading={loading}
            onClick={() => onReload()}
          >
            Reload
          </Button>
        </div>
      </div>
      <Table<WorkflowRecord>
        ref={tableContainer}
        columns={resolvedColumns}
        dataSource={records}
        expandable={resolvedExpandable}
        loading={loading}
        onChange={(_pagination, filters, _sorter, { currentDataSource }) => {
          hasActiveFilters.current = Object.values(filters).some((values) =>
            Boolean(values?.length)
          )
          setCurrentRecordCount(currentDataSource.length)
        }}
        onRow={(record) =>
          expandable.rowExpandable?.(record)
            ? { style: { cursor: 'pointer' } }
            : {}
        }
        pagination={pagination}
        rowKey="id"
        scroll={{
          x: currentRecordCount ? 'max-content' : undefined,
          y: '60vh',
        }}
        tableLayout="fixed"
      />
    </Space>
  )
}

export default RecordsTable
